import React from 'react';
import { useField } from 'formik';
import { WrappedBaseDatePicker, WrappedBaseDatePickerProps } from './BaseDatePicker';

type DatePickerProps = WrappedBaseDatePickerProps['inputProps'];
type FieldWrapperProps = WrappedBaseDatePickerProps['wrapperProps'];

export interface DatepickerInputProps {
  /** full props for the underlying Datepicker component */
  inputProps?: Omit<
    DatePickerProps,
    'value' | 'onChange' | 'onBlur' | 'disabled' | 'invalid' | 'name' | 'minDate' | 'maxDate'
  >;
  /** full props for the underlying FieldWrapper component */
  wrapperProps?: FieldWrapperProps;
  /** name of the form field in the Formik form values */
  name: string;
  /** disable the selection tag buttons */
  disabled?: boolean;
  /** label for the form field */
  label: FieldWrapperProps['label'];
  /** help text for the form field */
  helpText?: FieldWrapperProps['helpText'];
  /** help text for the form field but above the tags */
  helpTextAbove?: FieldWrapperProps['helpText'];
  /** tooltip for the form field */
  tooltip?: FieldWrapperProps['tooltip'];
  /** requires the field to be touched before it shows a validation error, defaults to false */
  validationRequiresTouched?: boolean;
  /** minDate the "earliest" selectable value, anything before this will be disabled
   * This should be an ISO date string
   */
  minDate?: string;
  /** maxDate the "latest" selectable value, anything after this will be disabled
   * This should be an ISO date string
   */
  maxDate?: string;
}

/**
 * A datepicker for a single calendar date, connected to formik form state
 * @name DatePickerInput
 * @description this should be used as the normal component for a single date field for all Formik forms.
 * it will automatically render any validation errors and will correctly set a11y roles for the elements
 *
 * https://www.figma.com/design/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=975-1680&m=dev
 *
 * @example
 * <DatePickerInput
 *  name="startDate"
 *  label={t('common:my.label.key')}
 * />
 */
export const DatePickerInput = ({
  disabled,
  helpText,
  helpTextAbove,
  inputProps,
  label,
  name,
  tooltip,
  validationRequiresTouched = false,
  wrapperProps,
  minDate,
  maxDate,
}: DatepickerInputProps) => {
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error, touched } = meta;
  const hasError = !!error;
  const isTouched = !!touched;
  const isInvalid = !validationRequiresTouched ? hasError : hasError && isTouched;
  const fieldValidation = isInvalid ? { invalid: true } : {};
  const wrapperValidation = isInvalid ? { validation: { valid: !hasError, message: error?.toString() } } : {};
  const inputValue = formikValue ?? '';

  const onChange = (newVal: string) => {
    helpers.setValue(newVal);
  };

  return (
    <WrappedBaseDatePicker
      inputProps={{
        ...inputProps,
        ...fieldValidation,
        disabled,
        onBlur,
        onChange,
        value: inputValue,
        minDate,
        maxDate,
        name,
      }}
      wrapperProps={{
        ...wrapperProps,
        ...wrapperValidation,
        disabled,
        helpText,
        helpTextAbove,
        id: name,
        label,
        tooltip,
      }}
    />
  );
};
